import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { useMediaQuery } from 'react-responsive';

// Palette de couleurs Corporate Premium (Dark Gray & Beige)
const colors = {
  bgDark: '#121214',
  bgMedium: '#1C1C1F',
  bgLight: '#252529',
  beigeLight: '#F5EFEB',
  beigeMain: '#D4C5B0',
  beigeDark: '#A3947F',
  textGray: '#A0A0A5',
  border: 'rgba(212, 197, 176, 0.15)',
};

const DroitMinier = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [activeImage, setActiveImage] = useState(0);
  const [activeService, setActiveService] = useState(0);
  const [showStats, setShowStats] = useState(false);
  
  const isMobile = useMediaQuery({ query: '(max-width: 768px)' });
  const isTablet = useMediaQuery({ query: '(max-width: 1024px)' });

  const galleryImages = [
    { 
      src: '/images/M1.webp', 
      title: 'Permis miniers',
      description: 'Acquisition et gestion des autorisations',
      icon: '📜'
    },
    { 
      src: '/images/M2.jpg', 
      title: 'Conformité environnementale',
      description: 'Réglementation et études d\'impact',
      icon: '🌱'
    },
    { 
      src: '/images/M3.jpg', 
      title: 'Négociations contractuelles',
      description: 'Contrats miniers et partenariats',
      icon: '🤝'
    },
    { 
      src: '/images/M4.jpg', 
      title: 'Droits fonciers',
      description: 'Sécurisation des titres miniers',
      icon: '🏛️'
    },
  ];

  // Carrousel auto
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveImage((prev) => (prev + 1) % galleryImages.length);
    }, 6000); // Ralenti pour un côté plus solennel
    return () => clearInterval(interval);
  }, [galleryImages.length]);

  const services = [
    {
      id: 1,
      title: 'Prospection & Permis',
      description: 'Accompagnement complet dans l\'obtention et la gestion des autorisations minières.',
      icon: '🔍',
      features: [
        'Due diligence juridique',
        'Négociation avec les autorités',
        'Gestion des permis',
        'Renouvellement des autorisations'
      ]
    },
    {
      id: 2,
      title: 'Fiscalité Minière',
      description: 'Optimisation fiscale et conformité réglementaire pour les activités minières.',
      icon: '⚖️',
      features: [
        'Stratégie fiscale',
        'Conformité déclarative',
        'Litiges administratifs',
        'Optimisation des redevances'
      ]
    },
    {
      id: 3,
      title: 'Environnement & RSE',
      description: 'Gestion des impacts environnementaux et conformité au développement durable.',
      icon: '🌍',
      features: [
        'Études d\'impact environnemental',
        'Gestion des risques écologiques',
        'Conformité RSE',
        'Rapports de durabilité'
      ]
    }
  ];

  const processSteps = [
    { number: '01', title: 'Analyse Juridique', description: 'Étude approfondie du cadre légal' },
    { number: '02', title: 'Stratégie', description: 'Planification des démarches administratives' },
    { number: '03', title: 'Mise en Œuvre', description: 'Exécution et suivi des procédures' },
    { number: '04', title: 'Conformité', description: 'Surveillance continue des obligations' }
  ];

  // Calcul des styles responsives
  const computedStyles = useMemo(() => {
    return {
      section: {
        position: 'relative',
        background: `linear-gradient(180deg, ${colors.bgDark} 0%, #17171A 50%, ${colors.bgDark} 100%)`,
        color: colors.beigeLight,
        minHeight: '100vh',
        overflow: 'hidden',
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
      },
      
      animatedBackground: {
        position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
        pointerEvents: 'none', zIndex: 1, overflow: 'hidden',
      },
      
      noiseOverlay: {
        position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)' opacity='0.04'/%3E%3C/svg%3E")`,
        pointerEvents: 'none', zIndex: 1,
      },
      
      gradientOrb: {
        position: 'absolute', top: '10%', right: '-10%',
        width: '50vw', height: '50vw',
        background: `radial-gradient(circle, rgba(212, 197, 176, 0.03) 0%, transparent 60%)`,
        filter: 'blur(80px)',
      },
      
      hero: {
        position: 'relative', zIndex: 2,
        padding: isMobile ? '6rem 1.5rem' : isTablet ? '8rem 3rem' : '10rem 5%',
        maxWidth: '1440px', margin: '0 auto',
      },
      
      heroContent: {
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : '1.2fr 1fr',
        gap: isMobile ? '3rem' : '5rem',
        alignItems: 'center',
      },
      
      titleWrapper: {
        position: 'relative', marginBottom: isMobile ? '2rem' : '3rem',
      },
      
      titleLine: {
        height: '1px',
        background: `linear-gradient(90deg, ${colors.beigeMain}, transparent)`,
        marginBottom: isMobile ? '1.5rem' : '2rem',
        width: isMobile ? '60px' : '100px',
      },
      
      mainTitle: {
        fontSize: isMobile ? '2.5rem' : isTablet ? '3.2rem' : '4.5rem',
        lineHeight: 1.1,
        fontWeight: 300,
        marginBottom: isMobile ? '1rem' : '1.5rem',
        color: colors.beigeLight,
        letterSpacing: '-1px',
      },
      
      subtitle: {
        fontSize: isMobile ? '1.2rem' : isTablet ? '1.4rem' : '1.8rem',
        color: colors.beigeMain,
        fontWeight: 400,
        fontStyle: 'italic',
        display: 'block',
        marginTop: isMobile ? '0.5rem' : '1rem',
      },
      
      heroTextWrapper: {
        display: 'flex', flexDirection: 'column', gap: '1.5rem',
        marginBottom: '3rem',
      },
      
      heroText: {
        fontSize: isMobile ? '1rem' : '1.1rem',
        lineHeight: 1.8,
        color: colors.textGray,
        paddingLeft: '1.5rem',
        borderLeft: `1px solid ${colors.border}`,
        fontWeight: 300,
      },
      
      statsContainer: {
        display: 'grid',
        gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
        gap: '1.5rem',
        paddingTop: '2rem',
        borderTop: `1px solid ${colors.border}`,
      },
      
      statCard: {
        display: 'flex', flexDirection: 'column', gap: '0.5rem',
      },
      
      statNumber: {
        fontSize: isMobile ? '1.8rem' : '2.2rem',
        fontWeight: 300,
        color: colors.beigeMain,
        fontFamily: "'Playfair Display', serif",
      },
      
      statLabel: {
        fontSize: '0.75rem',
        color: colors.textGray,
        textTransform: 'uppercase',
        letterSpacing: '1px',
      },
      
      heroImageContainer: {
        position: 'relative',
        padding: '20px', // Espace pour le cadre
      },
      
      imageBackground: {
        position: 'absolute',
        top: '20px', left: '20px', right: '-20px', bottom: '-20px',
        border: `1px solid ${colors.beigeDark}`,
        borderRadius: '2px',
        zIndex: -1,
      },
      
      imageWrapper: {
        position: 'relative',
        height: isMobile ? '350px' : isTablet ? '450px' : '600px',
        borderRadius: '2px',
        overflow: 'hidden',
      },
      
      image: {
        width: '100%', height: '100%',
        objectFit: 'cover',
        filter: 'grayscale(25%) contrast(1.1) brightness(0.85)',
      },
      
      imageOverlayContent: {
        position: 'absolute', bottom: 0, left: 0, right: 0,
        padding: '2rem',
        background: `linear-gradient(to top, rgba(18,18,20,0.95) 0%, transparent 100%)`,
        zIndex: 2,
      },
      
      overlayTitle: {
        fontSize: '1.2rem', color: colors.beigeMain, fontWeight: 400,
        marginBottom: '0.5rem', fontFamily: "'Playfair Display', serif",
      },
      
      overlayText: {
        fontSize: '0.9rem', color: colors.textGray, fontWeight: 300,
      },
      
      carouselIndicators: {
        display: 'flex', justifyContent: 'flex-start', gap: '0.8rem',
        marginTop: '1.5rem', paddingLeft: '20px',
      },
      
      indicator: {
        width: '30px', height: '2px',
        background: colors.border,
        border: 'none', cursor: 'pointer', padding: 0,
        transition: 'background 0.3s ease',
      },
      
      activeIndicator: {
        background: colors.beigeMain,
      },
      
      // Services Section
      servicesSection: {
        position: 'relative', zIndex: 2,
        padding: isMobile ? '4rem 1.5rem' : '8rem 5%',
        background: colors.bgDark,
        borderTop: `1px solid ${colors.border}`,
      },
      
      sectionHeader: {
        textAlign: 'center', marginBottom: isMobile ? '3rem' : '5rem',
      },
      
      sectionTitle: {
        fontSize: isMobile ? '2rem' : '3rem',
        fontWeight: 300, color: colors.beigeLight,
        marginBottom: '1rem', letterSpacing: '-0.5px',
      },
      
      titleHighlight: {
        color: colors.beigeMain, fontStyle: 'italic',
      },
      
      sectionSubtitle: {
        fontSize: '1.1rem', color: colors.textGray,
        maxWidth: '600px', margin: '0 auto', fontWeight: 300,
      },
      
      servicesGrid: {
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
        gap: '2rem', maxWidth: '1200px', margin: '0 auto',
      },
      
      serviceCard: {
        background: colors.bgMedium,
        borderRadius: '2px',
        padding: isMobile ? '2rem' : '2.5rem',
        border: `1px solid ${colors.border}`,
        transition: 'all 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
        display: 'flex', flexDirection: 'column', height: '100%',
      },
      
      serviceHeader: {
        display: 'flex', alignItems: 'center', gap: '1.5rem',
        marginBottom: '1.5rem', paddingBottom: '1.5rem',
        borderBottom: `1px solid ${colors.border}`,
      },
      
      serviceIcon: {
        fontSize: '2rem',
        color: colors.beigeMain,
        filter: 'grayscale(100%) sepia(100%) hue-rotate(5deg) saturate(300%) brightness(0.9)', // Rend les emojis dorés
      },
      
      serviceTitle: {
        fontSize: '1.3rem', fontWeight: 400, color: colors.beigeLight,
      },
      
      serviceDescription: {
        fontSize: '0.95rem', color: colors.textGray,
        lineHeight: 1.6, marginBottom: '2rem', flex: 1, fontWeight: 300,
      },
      
      serviceFeatures: {
        listStyle: 'none', padding: 0, margin: 0,
        display: 'flex', flexDirection: 'column', gap: '0.8rem',
      },
      
      serviceFeature: {
        fontSize: '0.9rem', color: colors.beigeLight,
        display: 'flex', alignItems: 'center', gap: '0.8rem', fontWeight: 300,
      },
      
      featureIcon: {
        color: colors.beigeDark, fontSize: '1rem',
      },
      
      // Gallery Section
      gallerySection: {
        position: 'relative', zIndex: 2,
        padding: isMobile ? '4rem 1.5rem' : '8rem 5%',
        background: colors.bgLight,
      },
      
      galleryGrid: {
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : isTablet ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
        gap: '1.5rem', maxWidth: '1440px', margin: '0 auto',
      },
      
      galleryCard: {
        background: colors.bgMedium,
        border: `1px solid ${colors.border}`,
        borderRadius: '2px',
        overflow: 'hidden',
        height: '380px',
        display: 'flex', flexDirection: 'column',
      },
      
      cardImageContainer: {
        position: 'relative', height: '200px', overflow: 'hidden',
      },
      
      cardContent: {
        padding: '1.5rem', flex: 1,
        display: 'flex', flexDirection: 'column',
      },
      
      cardHeader: {
        display: 'flex', alignItems: 'center', gap: '1rem',
        marginBottom: '0.8rem',
      },
      
      cardTitle: {
        fontSize: '1.1rem', fontWeight: 400, color: colors.beigeLight, margin: 0,
      },
      
      cardDescription: {
        fontSize: '0.9rem', color: colors.textGray,
        lineHeight: 1.6, flex: 1, fontWeight: 300,
      },
      
      // Process Section
      processSection: {
        position: 'relative', zIndex: 2,
        padding: isMobile ? '4rem 1.5rem' : '8rem 5%',
        background: colors.bgDark,
        borderTop: `1px solid ${colors.border}`,
      },
      
      processSteps: {
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        justifyContent: 'space-between',
        position: 'relative',
        gap: isMobile ? '3rem' : 0,
        maxWidth: '1200px', margin: '4rem auto 0',
      },
      
      processConnector: {
        position: 'absolute',
        top: '30px', left: '10%', right: '10%', height: '1px',
        background: colors.border, zIndex: 1,
        display: isMobile ? 'none' : 'block',
      },
      
      processStep: {
        position: 'relative', zIndex: 2, textAlign: 'center', flex: 1,
      },
      
      stepCircle: {
        width: '60px', height: '60px',
        background: colors.bgDark,
        border: `1px solid ${colors.beigeMain}`,
        margin: '0 auto 1.5rem',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: '1.2rem', fontWeight: '400', color: colors.beigeMain,
        fontFamily: "'Playfair Display', serif",
      },
      
      stepTitle: {
        fontSize: '1.1rem', fontWeight: 400, color: colors.beigeLight,
        marginBottom: '0.5rem',
      },
      
      stepDescription: {
        fontSize: '0.9rem', color: colors.textGray, fontWeight: 300,
        maxWidth: '200px', margin: '0 auto',
      },
    };
  }, [isMobile, isTablet]);

  // Animations Premium (Bézier Curve)
  const premiumTransition = { duration: 1, ease: [0.16, 1, 0.3, 1] };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.2, delayChildren: 0.1 } }
  };

  const fadeUpVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: premiumTransition }
  };

  useEffect(() => {
    const timer = setTimeout(() => setShowStats(true), 1000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <>
      <Helmet>
        <title>Droit Minier | KMJ & Associés</title>
        <meta name="description" content="Expertise en droit minier : permis miniers, fiscalité, environnement, contrats. Accompagnement complet pour vos projets miniers." />
      </Helmet>

      <motion.section 
        style={computedStyles.section}
        initial="hidden" animate="visible" variants={containerVariants}
      >
        {/* Background Overlay */}
        <div style={computedStyles.animatedBackground}>
          <div style={computedStyles.gradientOrb} />
          <div style={computedStyles.noiseOverlay} />
        </div>

        {/* Hero Section */}
        <div style={computedStyles.hero}>
          <motion.div style={computedStyles.heroContent} variants={containerVariants}>
            <motion.div variants={fadeUpVariants}>
              <div style={computedStyles.titleWrapper}>
                <div style={computedStyles.titleLine} />
                <motion.h1 style={computedStyles.mainTitle}>
                  Droit Minier
                  <span style={computedStyles.subtitle}>
                    Expertise stratégique pour vos projets.
                  </span>
                </motion.h1>
              </div>
              
              <motion.div style={computedStyles.heroTextWrapper}>
                <p style={computedStyles.heroText}>
                  Le cabinet KMJ & Associés propose une expertise d'excellence en droit minier, 
                  accompagnant les entreprises industrielles dans toutes les phases de leurs projets, 
                  de la prospection à l'exploitation.
                </p>
                <p style={computedStyles.heroText}>
                  Nous élaborons des stratégies juridiques robustes pour garantir 
                  la conformité des activités extractives et optimiser la fiscalité spécifique 
                  au secteur environnemental et contractuel.
                </p>
              </motion.div>

              {/* Stats */}
              <motion.div style={computedStyles.statsContainer}>
                {[
                  { number: '50+', label: 'Projets miniers' },
                  { number: '100+', label: 'Permis obtenus' },
                  { number: '15+', label: 'Années d\'expérience' },
                ].map((stat, index) => (
                  <div key={index} style={computedStyles.statCard}>
                    <motion.div 
                      style={computedStyles.statNumber}
                      initial={{ opacity: 0 }} animate={{ opacity: showStats ? 1 : 0 }}
                      transition={{ duration: 1.5, delay: 0.5 + (index * 0.2) }}
                    >
                      {stat.number}
                    </motion.div>
                    <div style={computedStyles.statLabel}>{stat.label}</div>
                  </div>
                ))}
              </motion.div>
            </motion.div>

            {/* Hero Image Carousel */}
            <motion.div style={computedStyles.heroImageContainer} variants={fadeUpVariants}>
              <div style={computedStyles.imageBackground} />
              <div style={computedStyles.imageWrapper}>
                <AnimatePresence mode="wait">
                  <motion.img
                    key={activeImage}
                    src={galleryImages[activeImage].src}
                    alt={galleryImages[activeImage].title}
                    style={computedStyles.image}
                    initial={{ opacity: 0, scale: 1.05 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.8, ease: "easeInOut" }}
                  />
                </AnimatePresence>
                <div style={computedStyles.imageOverlayContent}>
                  <motion.h3 
                    key={`title-${activeImage}`}
                    initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
                    style={computedStyles.overlayTitle}
                  >
                    {galleryImages[activeImage].title}
                  </motion.h3>
                  <motion.p 
                    key={`desc-${activeImage}`}
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}
                    style={computedStyles.overlayText}
                  >
                    {galleryImages[activeImage].description}
                  </motion.p>
                </div>
              </div>
              
              {/* Carousel Indicators (Lignes fines) */}
              <div style={computedStyles.carouselIndicators}>
                {galleryImages.map((_, index) => (
                  <button
                    key={index}
                    style={{
                      ...computedStyles.indicator,
                      ...(activeImage === index ? computedStyles.activeIndicator : {})
                    }}
                    onClick={() => setActiveImage(index)}
                  />
                ))}
              </div>
            </motion.div>
          </motion.div>
        </div>

        {/* Services Section */}
        <div style={computedStyles.servicesSection}>
          <motion.div style={computedStyles.sectionHeader} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUpVariants}>
            <h2 style={computedStyles.sectionTitle}>
              Nos <span style={computedStyles.titleHighlight}>Expertises</span>
            </h2>
            <p style={computedStyles.sectionSubtitle}>
              Des solutions complètes pour chaque aspect juridique de votre projet minier.
            </p>
          </motion.div>
          
          <div style={computedStyles.servicesGrid}>
            {services.map((service, index) => (
              <motion.div
                key={service.id}
                style={computedStyles.serviceCard}
                initial="hidden" whileInView="visible" viewport={{ once: true }}
                variants={{ hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0, transition: { ...premiumTransition, delay: index * 0.1 } } }}
                whileHover={{ y: -5, borderColor: colors.beigeDark, boxShadow: '0 10px 30px rgba(0,0,0,0.5)' }}
              >
                <div style={computedStyles.serviceHeader}>
                  <div style={computedStyles.serviceIcon}>{service.icon}</div>
                  <h3 style={computedStyles.serviceTitle}>{service.title}</h3>
                </div>
                <p style={computedStyles.serviceDescription}>{service.description}</p>
                <ul style={computedStyles.serviceFeatures}>
                  {service.features.map((feature, i) => (
                    <li key={i} style={computedStyles.serviceFeature}>
                      <span style={computedStyles.featureIcon}>✓</span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Gallery Section */}
        <div style={computedStyles.gallerySection}>
          <motion.div style={computedStyles.sectionHeader} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUpVariants}>
            <h2 style={computedStyles.sectionTitle}>
              Domaines d'<span style={computedStyles.titleHighlight}>Intervention</span>
            </h2>
          </motion.div>
          
          <div style={computedStyles.galleryGrid}>
            {galleryImages.map((item, index) => (
              <motion.div
                key={index}
                style={computedStyles.galleryCard}
                initial="hidden" whileInView="visible" viewport={{ once: true }}
                variants={{ hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0, transition: { ...premiumTransition, delay: index * 0.1 } } }}
                whileHover={{ y: -5, borderColor: colors.beigeDark }}
              >
                <div style={computedStyles.cardImageContainer}>
                  <img src={item.src} alt={item.title} style={computedStyles.image} loading="lazy" />
                </div>
                <div style={computedStyles.cardContent}>
                  <div style={computedStyles.cardHeader}>
                    <h3 style={computedStyles.cardTitle}>{item.title}</h3>
                  </div>
                  <p style={computedStyles.cardDescription}>{item.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Process Section */}
        <div style={computedStyles.processSection}>
          <motion.div style={computedStyles.sectionHeader} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUpVariants}>
            <h2 style={computedStyles.sectionTitle}>
              Notre <span style={computedStyles.titleHighlight}>Processus</span>
            </h2>
          </motion.div>

          <div style={computedStyles.processSteps}>
            <div style={computedStyles.processConnector} />
            {processSteps.map((step, index) => (
              <motion.div
                key={index} style={computedStyles.processStep}
                initial="hidden" whileInView="visible" viewport={{ once: true }}
                variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0, transition: { ...premiumTransition, delay: index * 0.2 } } }}
              >
                <div style={computedStyles.stepCircle}>{step.number}</div>
                <h3 style={computedStyles.stepTitle}>{step.title}</h3>
                <p style={computedStyles.stepDescription}>{step.description}</p>
              </motion.div>
            ))}
          </div>
        </div>

      </motion.section>
    </>
  );
};

export default DroitMinier;